/**
 * Error handling utilities for consistent error management across the application
 */

/**
 * Get user-friendly error message based on error type and status
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred (e.g., 'institution', 'comparison')
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error, context = 'general') => {
  // Handle network errors
  if (!error.response) {
    return 'Netværksfejl. Tjek din internetforbindelse og prøv igen.';
  }

  const { status, data } = error.response;

  // Handle specific HTTP status codes
  switch (status) {
    case 400:
      return 'Ugyldig anmodning. Prøv venligst igen.';
    
    case 401:
      return 'Du er ikke autoriseret til at se denne side. Log venligst ind.';
    
    case 403:
      return 'Adgang nægtet. Du har ikke tilladelse til at se denne side.';
    
    case 404:
      switch (context) {
        case 'institution':
          return 'Institutionen blev ikke fundet. Den kan være blevet fjernet eller linket er forkert.';
        case 'comparison':
          return 'Sammenligningen blev ikke fundet.';
        case 'page':
          return 'Siden blev ikke fundet.';
        default:
          return 'Den ønskede ressource blev ikke fundet.';
      }
    
    case 429:
      return 'For mange anmodninger. Vent venligst et øjeblik og prøv igen.';
    
    case 500:
      return 'Serverfejl. Prøv venligst igen senere.';
    
    case 502:
    case 503:
    case 504:
      return 'Tjenesten er midlertidigt ikke tilgængelig. Prøv venligst igen senere.';
    
    default:
      // Try to extract error message from response data
      if (data && data.message) {
        return data.message;
      }
      if (data && data.error) {
        return data.error;
      }
      
      return 'Der opstod en uventet fejl. Prøv venligst igen.';
  }
};

/**
 * Check if error is a 404 error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a 404 error
 */
export const isNotFoundError = (error) => {
  return error.response?.status === 404;
};

/**
 * Check if error is a network error (no response)
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a network error
 */
export const isNetworkError = (error) => {
  return !error.response;
};

/**
 * Check if error is a server error (5xx status codes)
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a server error
 */
export const isServerError = (error) => {
  const status = error.response?.status;
  return status >= 500 && status < 600;
};

/**
 * Check if error is a client error (4xx status codes)
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a client error
 */
export const isClientError = (error) => {
  const status = error.response?.status;
  return status >= 400 && status < 500;
};

/**
 * Log error with context for debugging
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @param {string} component - Component name where the error occurred
 */
export const logError = (error, context = 'general', component = 'unknown') => {
  if (process.env.NODE_ENV === 'development') {
    console.group(`Error in ${component} - ${context}`);
    console.error('Error details:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    console.error('Error data:', error.response?.data);
    console.groupEnd();
  }
  
  // In production, you might want to send errors to a logging service
  // logToService(error, context, component);
};

const CHECKOUT_FALLBACK =
  'Betalingen kunne ikke startes. Ingen beløb er trukket. Prøv igen.';

/**
 * User-visible message when Stripe Checkout session creation fails.
 */
export const getCheckoutErrorMessage = (error) => {
  const apiError = error?.response?.data?.error;
  if (typeof apiError === 'string' && apiError.trim()) {
    if (/temporarily disabled/i.test(apiError)) {
      return 'Betaling er midlertidigt slået fra. Prøv igen senere.';
    }
    if (/not configured|not available/i.test(apiError)) {
      return 'Betaling er ikke tilgængelig lige nu. Prøv igen senere.';
    }
  }
  if (!error?.response) {
    return 'Kunne ikke oprette forbindelse til betalingen. Tjek din internetforbindelse og prøv igen.';
  }
  if (error.response.status === 401) {
    return 'Din session er udløbet. Log ind igen for at fortsætte betalingen.';
  }
  if (error.response.status >= 500) {
    return 'Betalingsserveren svarede ikke. Prøv igen om et øjeblik.';
  }
  return CHECKOUT_FALLBACK;
};
